// AcademicYear.js
import React, { useState, useEffect, useRef,useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import api from '../../api';
import moment from 'moment';
import CalendarGrid from './components/CalendarGrid';
import AcademicYearCard from './components/AcademicYearCard';
import AcademicYearForm from './components/AcademicYearForm';
import DeleteModal from './components/DeleteModal';
import EventModal from './components/EventModal';
import SemesterSelector from './components/SemesterSelector';
import EventList from './components/EventList';
import StatusHistoryModal from './components/StatusHistoryModal';
import ArchivedAcademicYears from './components/ArchivedAcademicYears';
import RollbackModal from './components/RollbackModal';
import DuplicateConfirmationModal from './components/DuplicateConfirmationModal';

import {
  generateSemesters,
  renderSemesterDetails,
  extractDate
} from './utils/academicYearUtils';

const AcademicYear = () => {
  const location = useLocation();
  const { tenantId, userId } = location.state || {};

  // State management
  const [academicYears, setAcademicYears] = useState([]);
  const [, setActiveAcademicYears] = useState([]);     const [inactiveAcademicYears, setInactiveAcademicYears] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [editAcademicYearId, setEditAcademicYearId] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteAcademicYearId, setDeleteAcademicYearId] = useState(null);
  const [showArchivedModal, setShowArchivedModal] = useState(false);
  const [viewMode, setViewMode] = useState('active'); // 'active', 'inactive', 'all'

  // Recovery and Rollback state
  const [showStatusHistory, setShowStatusHistory] = useState(false);
  const [statusHistoryData, setStatusHistoryData] = useState(null);
  const [showArchivedYears, setShowArchivedYears] = useState(false);
  const [showRollbackModal, setShowRollbackModal] = useState(false);
  const [rollbackAcademicYearId, setRollbackAcademicYearId] = useState(null);

  // Duplicate confirmation modal state
  const [showDuplicateModal, setShowDuplicateModal] = useState(false);
  const [conflictInfo, setConflictInfo] = useState(null);
  const [duplicateType, setDuplicateType] = useState(null);
  const [pendingFormData, setPendingFormData] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Calendar state
  const [showCalendar, setShowCalendar] = useState(false);
  const [selectedAcademicYearId, setSelectedAcademicYearId] = useState(null);
  const [calendarConfig, setCalendarConfig] = useState(null);
  const [calendarEvents, setCalendarEvents] = useState([]);
  const [selectedSemesterType, setSelectedSemesterType] = useState(null);
  const [showAddEventModal, setShowAddEventModal] = useState(false);
  const [newEvent, setNewEvent] = useState({
    eventName: '',
    eventType: 'academic',
    startDate: '',
    endDate: '',
    description: '',
  });
  const [editEventId, setEditEventId] = useState(null);
  const [showDeleteEventModal, setShowDeleteEventModal] = useState(false);
  const [eventToDelete, setEventToDelete] = useState(null);
  const [unblockedSundays, setUnblockedSundays] = useState([]);
  const [showUnblockModal, setShowUnblockModal] = useState(false);
  const [selectedSunday, setSelectedSunday] = useState(null);

  // Form state
  const [formData, setFormData] = useState({
    name: '',
    label: '', // NEW: Add label field
    startDate: '',
    endDate: '',
    isSemesterBased: false,
    numberOfSemesters: 2,
    oddSemesterDates: { startDate: '', endDate: '' },
    evenSemesterDates: { startDate: '', endDate: '' }
  });

  // Form validation state
  const [formErrors, setFormErrors] = useState({});
  const [touchedFields, setTouchedFields] = useState({});

  const nameInputRef = useRef(null);
  const eventListRef = useRef(null);
  const eventsRef = useRef(null);
  const modalBodyRef = useRef(null);
  const successRef = useRef(null);
  const errorRef = useRef(null);

  // Validation rules
  const validateField = (name, value, formData) => {
    const errors = {};
    
    switch (name) {
      case 'name':
        if (!value.trim()) {
          errors.name = 'Academic year name is required';
        } else if (value.trim().length < 3) {
          errors.name = 'Name must be at least 3 characters long';
        }
        break;
        
      // NEW: Add label validation
      case 'label':
        if (!value || !value.trim()) {
          errors.label = 'Label is required (e.g., "UG", "PG", "Engineering")';
        } else if (value.trim().length > 50) {
          errors.label = 'Label cannot exceed 50 characters';
        }
        break;
        
      case 'startDate':
        if (!value) {
          errors.startDate = 'Start date is required';
        } else if (formData.endDate && new Date(value) > new Date(formData.endDate)) {
          errors.startDate = 'Start date cannot be after end date';
        }
        break;
        
      case 'endDate':
        if (!value) {
          errors.endDate = 'End date is required';
        } else if (formData.startDate && new Date(value) < new Date(formData.startDate)) {
          errors.endDate = 'End date cannot be before start date';
        }
        break;
        
      case 'numberOfSemesters':
        if (formData.isSemesterBased && (!value || value < 1 || value > 20)) {
          errors.numberOfSemesters = 'Number of semesters must be between 1 and 20';
        }
        break;
        
      case 'oddSemester.startDate':
        if (formData.isSemesterBased && !value) {
          errors.oddSemesterDates = { ...errors.oddSemesterDates, startDate: 'Odd semester start date is required' };
        }
        break;
        
      case 'oddSemester.endDate':
        if (formData.isSemesterBased && !value) {
          errors.oddSemesterDates = { ...errors.oddSemesterDates, endDate: 'Odd semester end date is required' };
        } else if (formData.isSemesterBased && formData.oddSemesterDates.startDate && new Date(value) < new Date(formData.oddSemesterDates.startDate)) {
          errors.oddSemesterDates = { ...errors.oddSemesterDates, endDate: 'Odd semester end date cannot be before start date' };
        }
        break;
        
      case 'evenSemester.startDate':
        if (formData.isSemesterBased && formData.numberOfSemesters >= 2 && !value) {
          errors.evenSemesterDates = { ...errors.evenSemesterDates, startDate: 'Even semester start date is required' };
        }
        break;
        
      case 'evenSemester.endDate':
        if (formData.isSemesterBased && formData.numberOfSemesters >= 2 && !value) {
          errors.evenSemesterDates = { ...errors.evenSemesterDates, endDate: 'Even semester end date is required' };
        } else if (formData.isSemesterBased && formData.numberOfSemesters >= 2 && formData.evenSemesterDates.startDate && new Date(value) < new Date(formData.evenSemesterDates.startDate)) {
          errors.evenSemesterDates = { ...errors.evenSemesterDates, endDate: 'Even semester end date cannot be before start date' };
        }
        break;
        
      default:
        break;
    }
    
    return errors;
  };

  // Validate entire form
  const validateForm = (data) => {
    const errors = {};
    
    // Basic fields
    if (!data.name.trim()) errors.name = 'Academic year name is required';
    
    // NEW: Validate label
    if (!data.label || !data.label.trim()) {
      errors.label = 'Label is required (e.g., "UG", "PG", "Engineering")';
    } else if (data.label.trim().length > 50) {
      errors.label = 'Label cannot exceed 50 characters';
    }
    
    if (!data.startDate) errors.startDate = 'Start date is required';
    if (!data.endDate) errors.endDate = 'End date is required';
    
    if (data.startDate && data.endDate && new Date(data.startDate) > new Date(data.endDate)) {
      errors.endDate = 'End date cannot be before start date';
    }
    
    // Semester-based validation
    if (data.isSemesterBased) {
      if (!data.numberOfSemesters || data.numberOfSemesters < 1 || data.numberOfSemesters > 20) {
        errors.numberOfSemesters = 'Number of semesters must be between 1 and 4';
      }
      
      // Odd semester validation
      if (!data.oddSemesterDates.startDate) {
        errors.oddSemesterDates = { ...errors.oddSemesterDates, startDate: 'Odd semester start date is required' };
      }
      if (!data.oddSemesterDates.endDate) {
        errors.oddSemesterDates = { ...errors.oddSemesterDates, endDate: 'Odd semester end date is required' };
      }
      if (data.oddSemesterDates.startDate && data.oddSemesterDates.endDate && 
          new Date(data.oddSemesterDates.startDate) > new Date(data.oddSemesterDates.endDate)) {
        errors.oddSemesterDates = { ...errors.oddSemesterDates, endDate: 'Odd semester end date cannot be before start date' };
      }
      
      // Even semester validation (only if 2 or more semesters)
      if (data.numberOfSemesters >= 2) {
        if (!data.evenSemesterDates.startDate) {
          errors.evenSemesterDates = { ...errors.evenSemesterDates, startDate: 'Even semester start date is required' };
        }
        if (!data.evenSemesterDates.endDate) {
          errors.evenSemesterDates = { ...errors.evenSemesterDates, endDate: 'Even semester end date is required' };
        }
        if (data.evenSemesterDates.startDate && data.evenSemesterDates.endDate && 
            new Date(data.evenSemesterDates.startDate) > new Date(data.evenSemesterDates.endDate)) {
          errors.evenSemesterDates = { ...errors.evenSemesterDates, endDate: 'Even semester end date cannot be before start date' };
        }
      }
    }
    
    return errors;
  };

  // Auto scroll & focus status messages when they appear
  useEffect(() => {
    if (error || successMessage) {
      setTimeout(() => {
        const elementToFocus = error ? errorRef.current : successRef.current;
        
        if (elementToFocus) {
          try {
            elementToFocus.scrollIntoView({ 
              behavior: 'smooth', 
              block: 'center',
              inline: 'nearest'
            });
            elementToFocus.focus({ preventScroll: true });
          } catch (scrollError) {
            elementToFocus.scrollIntoView();
            elementToFocus.focus();
          }
        }
      }, 100);
    }
  }, [error, successMessage]);

  // Scroll handler called by CalendarGrid to show events list inside this modal
  const handleViewAllEvents = () => {
    setTimeout(() => {
      const target =
        eventsRef.current ||
        document.getElementById('events-section') ||
        document.querySelector('[data-events-section]');

      const container = modalBodyRef.current || document.scrollingElement || document.documentElement;

      if (target && container) {
        const top =
          target.getBoundingClientRect().top -
          container.getBoundingClientRect().top +
          container.scrollTop;
        container.scrollTo({ top, behavior: 'smooth' });
        return;
      }

      if (target) {
        target.scrollIntoView({ behavior: 'smooth', block: 'start' });
        return;
      }

      window.scrollTo({ top: document.body.scrollHeight, behavior: 'smooth' });
    }, 120);
  };



// Fetch academic years based on view mode (memoized for useEffect)
const fetchAcademicYears = useCallback(async (mode = 'active') => {
  if (!tenantId) return;
  setLoading(true);
  setError(null);
  try {
    const token = localStorage.getItem('token');
    let url = `/api/academic-year/`;

    if (mode === 'active') {
      url = `/api/academic-year/all-active?tenantId=${tenantId}`;
    } else if (mode === 'inactive') {
      url = `/api/academic-year/inactive?tenantId=${tenantId}`;
    } else {
      url = `/api/academic-year/?tenantId=${tenantId}&includeArchived=false`;
    }

    const response = await api.get(url, {
      headers: { Authorization: `Bearer ${token}` },
    });

    const years = response.data.academicYears;
    setAcademicYears(years);

    if (mode === 'active') {
      setActiveAcademicYears(years);
    } else if (mode === 'inactive') {
      setInactiveAcademicYears(years);
    }

  } catch (err) {
  console.error('AcademicYear fetch error:', err);
  setError(err.response?.data?.message || err.message || 'Failed to load academic years');
} finally {
    setLoading(false);
  }
}, [tenantId, setActiveAcademicYears]);

useEffect(() => {
  fetchAcademicYears(viewMode);
}, [tenantId, viewMode, fetchAcademicYears]);

  // Focus management
  useEffect(() => {
    if (showForm && nameInputRef.current) {
      nameInputRef.current.focus();
    }
  }, [showForm]);

  // Clear form errors when form is closed
  useEffect(() => {
    if (!showForm) {
      setFormErrors({});
      setTouchedFields({});
    }
  }, [showForm]);

  // Form submission with duplicate detection
  const handleSubmitAcademicYear = async (e) => {
    e.preventDefault();
    setError(null);
    setSuccessMessage(null);

    // Validate form before submission
    const validationErrors = validateForm(formData);
    if (Object.keys(validationErrors).length > 0) {
      setFormErrors(validationErrors);
      setTimeout(() => {
        const firstErrorElement = document.querySelector('.border-red-500');
        if (firstErrorElement) {
          firstErrorElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
          firstErrorElement.focus();
        }
      }, 100);
      return;
    }

    // Build payload
    const academicYearData = {
      tenantId,
      ...formData,
      label: formData.label.trim(), // NEW: Include label in payload
      createdBy: userId,
      startDate: formData.startDate ? new Date(formData.startDate).toISOString() : '',
      endDate: formData.endDate ? new Date(formData.endDate).toISOString() : '',
      oddSemesterDates: {
        startDate: formData.oddSemesterDates.startDate ? new Date(formData.oddSemesterDates.startDate).toISOString() : '',
        endDate: formData.oddSemesterDates.endDate ? new Date(formData.oddSemesterDates.endDate).toISOString() : ''
      },
      evenSemesterDates: {
        startDate: formData.evenSemesterDates.startDate ? new Date(formData.evenSemesterDates.startDate).toISOString() : '',
        endDate: formData.evenSemesterDates.endDate ? new Date(formData.evenSemesterDates.endDate).toISOString() : ''
      }
    };

    if (formData.isSemesterBased) {
      academicYearData.totalSemesters = formData.numberOfSemesters;
      academicYearData.semesters = generateSemesters(
        formData.numberOfSemesters,
        formData.oddSemesterDates,
        formData.evenSemesterDates
      );
    }

    // Submit without confirmation first to check for duplicates
    await submitAcademicYear(academicYearData, false);
  };

  // Main submission function
  const submitAcademicYear = async (academicYearData, confirm = false) => {
    setLoading(true);
    setError(null);

    try {
      const token = localStorage.getItem('token');
      let response;

      if (confirm) {
        academicYearData.confirm = true;
      }

      if (editAcademicYearId) {
        response = await api.put(
          `/api/academic-year/${editAcademicYearId}`,
          academicYearData,
          { headers: { Authorization: `Bearer ${token}` } }
        );
      } else {
        response = await api.post(
          '/api/academic-year/create',
          academicYearData,
          { headers: { Authorization: `Bearer ${token}` } }
        );
      }

      setSuccessMessage(response.data?.message || `Academic year ${editAcademicYearId ? 'updated' : 'created'} successfully`);
      await fetchAcademicYears(viewMode);
      resetForm();
      setShowDuplicateModal(false);
      setConflictInfo(null);
      setPendingFormData(null);

    } catch (err) {
      const status = err.response?.status;
      const data = err.response?.data;

      if (status === 409 && data?.requiresConfirmation) {
        // Show duplicate/conflict confirmation modal
        setConflictInfo(data.conflict);
        setDuplicateType(data.duplicateType);
        setPendingFormData(academicYearData);
        setShowDuplicateModal(true);
      } else {
        setError(data?.message || `Failed to ${editAcademicYearId ? 'update' : 'create'} academic year`);
      }
    } finally {
      setLoading(false);
    }
  };

  // Handle user confirmation to proceed with duplicate
  const handleConfirmDuplicate = async () => {
    if (!pendingFormData) return;
    
    setIsSubmitting(true);
    await submitAcademicYear(pendingFormData, true);
    setIsSubmitting(false);
  };

  // Handle cancel duplicate creation
  const handleCancelDuplicate = () => {
    setShowDuplicateModal(false);
    setConflictInfo(null);
    setPendingFormData(null);
    setDuplicateType(null);
  };

  // Form reset
  const resetForm = () => {
    setFormData({
      name: '',
      label: '', // NEW: Reset label
      startDate: '',
      endDate: '',
      isSemesterBased: false,
      numberOfSemesters: 2,
      oddSemesterDates: { startDate: '', endDate: '' },
      evenSemesterDates: { startDate: '', endDate: '' }
    });
    setFormErrors({});
    setTouchedFields({});
    setEditAcademicYearId(null);
    setShowForm(false);
    setPendingFormData(null);
  };

  // Field blur handler for validation
  const handleFieldBlur = (fieldName) => {
    setTouchedFields(prev => ({ ...prev, [fieldName]: true }));
    
    // Validate the field
    const fieldErrors = validateField(fieldName, formData[fieldName], formData);
    setFormErrors(prev => ({ ...prev, ...fieldErrors }));
  };

  // Semester field blur handler
  const handleSemesterFieldBlur = (semesterType, fieldName) => {
    const fullFieldName = `${semesterType}Semester.${fieldName}`;
    setTouchedFields(prev => ({ ...prev, [fullFieldName]: true }));
    
    const fieldErrors = validateField(
      fullFieldName, 
      formData[`${semesterType}SemesterDates`][fieldName], 
      formData
    );
    setFormErrors(prev => ({ ...prev, ...fieldErrors }));
  };

  // Edit handler
  const handleEditAcademicYear = (academicYear) => {
    setFormData({
      name: academicYear.name,
      label: academicYear.label || '',
      startDate: extractDate(academicYear.startDate),
      endDate: extractDate(academicYear.endDate),
      isSemesterBased: academicYear.isSemesterBased,
      numberOfSemesters: academicYear.semesters?.length || academicYear.numberOfSemesters || 2,
      oddSemesterDates: {
        startDate: extractDate(academicYear.oddSemesterDates?.startDate),
        endDate: extractDate(academicYear.oddSemesterDates?.endDate)
      },
      evenSemesterDates: {
        startDate: extractDate(academicYear.evenSemesterDates?.startDate),
        endDate: extractDate(academicYear.evenSemesterDates?.endDate)
      },
      // Load numberOfTerms and termLabels for non-semester-based AYs
      numberOfTerms: !academicYear.isSemesterBased ? (academicYear.terms?.length || academicYear.numberOfTerms || 0) : undefined,
      termLabels: !academicYear.isSemesterBased && academicYear.terms ? academicYear.terms.map(t => t.termLabel) : undefined
    });
    setEditAcademicYearId(academicYear.id || academicYear._id);
    setShowForm(true);
    setFormErrors({});
    setTouchedFields({});
  };

  // Delete handlers
  const openDeleteModal = (academicYearId) => {
    setDeleteAcademicYearId(academicYearId);
    setShowDeleteModal(true);
  };

  const closeDeleteModal = () => {
    setShowDeleteModal(false);
    setDeleteAcademicYearId(null);
  };

  const handleDeleteAcademicYear = async () => {
    if (!deleteAcademicYearId) return;
    setLoading(true);
    setError(null);
    setSuccessMessage(null);

    const token = localStorage.getItem('token');
    try {
      await api.delete(`/api/academic-year/${deleteAcademicYearId}`, {
        headers: { Authorization: `Bearer ${token}` },
        data: { 
          tenantId, 
          deletedBy: userId,
          reason: 'Manual archiving by user'
        },
      });
      setSuccessMessage('Academic year archived successfully. Data preserved for 2 years.');
      setTimeout(() => {
        fetchAcademicYears(viewMode);
        closeDeleteModal();
      }, 300);
    } catch (err) {
      setError(err.response?.data?.message || 'Error archiving academic year');
      setLoading(false);
    }
  };

  // Status Management Handlers
  const handleMarkAcademicYearCompleted = async (academicYearId) => {
    setLoading(true);
    setError(null);
    setSuccessMessage(null);

    const token = localStorage.getItem('token');
    try {
      await api.put(
        `/api/academic-year/${academicYearId}/complete`,
        { tenantId },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setSuccessMessage('Academic year marked as completed successfully');
      setTimeout(() => {
        fetchAcademicYears(viewMode);
      }, 300);
    } catch (err) {
      setError(err.response?.data?.message || 'Error marking academic year as completed');
    } finally {
      setLoading(false);
    }
  };

  const handleMarkSemesterTypeCompleted = async (academicYearId, semesterType) => {
    setLoading(true);
    setError(null);
    setSuccessMessage(null);

    const token = localStorage.getItem('token');
    try {
      await api.put(
        `/api/academic-year/${academicYearId}/complete-semester`,
        { tenantId, semesterType },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setSuccessMessage(`${semesterType} semester(s) marked as completed successfully`);
      setTimeout(() => {
        fetchAcademicYears(viewMode);
      }, 300);
    } catch (err) {
      setError(err.response?.data?.message || `Error marking ${semesterType} semester as completed`);
    } finally {
      setLoading(false);
    }
  };

  // NEW: Handle individual semester completion using semester ID
  const handleMarkIndividualSemesterCompleted = async (academicYearId, semesterId) => {
    setLoading(true);
    setError(null);
    setSuccessMessage(null);

    const token = localStorage.getItem('token');
    try {
      await api.put(
        `/api/academic-year/${academicYearId}/complete-semester/${semesterId}`,
        { tenantId },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      // Get semester name from response or find it in the current data
      const academicYear = academicYears.find(ay => (ay.id || ay._id) === academicYearId);
      const semester = academicYear?.semesters?.find(s => s._id === semesterId);
      const semesterName = semester?.semesterName || 'Semester';
      
      setSuccessMessage(`${semesterName} marked as completed successfully`);
      setTimeout(() => {
        fetchAcademicYears(viewMode);
      }, 300);
    } catch (err) {
      setError(err.response?.data?.message || `Error marking semester as completed`);
    } finally {
      setLoading(false);
    }
  };

  // Rollback and Recovery Handlers
  const handleRollbackAcademicYearStatus = async (academicYearId, targetStatus, reason) => {
    setLoading(true);
    setError(null);
    setSuccessMessage(null);

    const token = localStorage.getItem('token');
    try {
      await api.put(
        `/api/academic-year/${academicYearId}/rollback`,
        { 
          tenantId, 
          targetStatus, 
          reason,
          rolledBackBy: userId 
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setSuccessMessage(`Academic year rolled back to ${targetStatus} successfully`);
      setTimeout(() => {
        fetchAcademicYears(viewMode);
        setShowRollbackModal(false);
      }, 300);
    } catch (err) {
      setError(err.response?.data?.message || 'Error rolling back academic year status');
      setLoading(false);
    }
  };

  const handleRollbackSemesterTypeStatus = async (academicYearId, semesterType, targetStatus, reason) => {
    setLoading(true);
    setError(null);
    setSuccessMessage(null);

    const token = localStorage.getItem('token');
    try {
      await api.put(
        `/api/academic-year/${academicYearId}/rollback-semester-type`,
        { 
          tenantId, 
          semesterType,
          targetStatus, 
          reason,
          rolledBackBy: userId 
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setSuccessMessage(`${semesterType} semester(s) rolled back to ${targetStatus} successfully`);
      setTimeout(() => {
        fetchAcademicYears(viewMode);
        setShowRollbackModal(false);
      }, 300);
    } catch (err) {
      setError(err.response?.data?.message || 'Error rolling back semester status');
      setLoading(false);
    }
  };

  // NEW: Handle individual semester rollback using semester ID
  const handleRollbackSemester = async (academicYearId, semesterId, targetStatus = 'active', reason = 'Rollback semester') => {
    setLoading(true);
    setError(null);
    setSuccessMessage(null);

    const token = localStorage.getItem('token');
    try {
      await api.put(
        `/api/academic-year/${academicYearId}/rollback-semester/${semesterId}`,
        { 
          tenantId, 
          targetStatus, 
          reason,
          rolledBackBy: userId 
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      // Get semester name from response or find it in the current data
      const academicYear = academicYears.find(ay => (ay.id || ay._id) === academicYearId);
      const semester = academicYear?.semesters?.find(s => s._id === semesterId);
      const semesterName = semester?.semesterName || 'Semester';
      
      setSuccessMessage(`${semesterName} rolled back to ${targetStatus} successfully`);
      setTimeout(() => {
        fetchAcademicYears(viewMode);
      }, 300);
    } catch (err) {
      setError(err.response?.data?.message || 'Error rolling back semester status');
      setLoading(false);
    }
  };

  const openRollbackModal = (academicYearId) => {
    setRollbackAcademicYearId(academicYearId);
    setShowRollbackModal(true);
  };

  const closeRollbackModal = () => {
    setShowRollbackModal(false);
    setRollbackAcademicYearId(null);
  };

  const handleRestoreAcademicYear = async (academicYearId) => {
    setLoading(true);
    setError(null);
    setSuccessMessage(null);

    const token = localStorage.getItem('token');
    try {
      await api.put(
        `/api/academic-year/${academicYearId}/restore`,
        { 
          tenantId, 
          restoredBy: userId,
          targetStatus: 'inactive'
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setSuccessMessage('Academic year restored successfully');
      setTimeout(() => {
        fetchAcademicYears(viewMode);
        setShowArchivedYears(false);
      }, 300);
    } catch (err) {
      setError(err.response?.data?.message || 'Error restoring academic year');
    } finally {
      setLoading(false);
    }
  };

  const handleViewStatusHistory = async (academicYearId) => {
    setLoading(true);
    setError(null);

    const token = localStorage.getItem('token');
    try {
      const response = await api.get(
        `/api/academic-year/${academicYearId}/status-history?tenantId=${tenantId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      const academicYear = academicYears.find(ay => (ay.id || ay._id) === academicYearId);
      setStatusHistoryData({
        history: response.data.statusHistory,
        currentStatus: response.data.currentStatus,
        academicYearName: academicYear?.name || 'Academic Year'
      });
      setShowStatusHistory(true);
    } catch (err) {
      setError(err.response?.data?.message || 'Error fetching status history');
    } finally {
      setLoading(false);
    }
  };

  // Input change handler with validation
  const handleInputChange = (e) => {
    const { name, value, checked } = e.target;
    
    // Update form data
    if (name === 'isSemesterBased') {
      setFormData(prev => ({ ...prev, [name]: checked }));
      // Clear semester errors when disabling semester-based
      if (!checked) {
        setFormErrors(prev => ({
          ...prev,
          numberOfSemesters: undefined,
          oddSemesterDates: undefined,
          evenSemesterDates: undefined
        }));
      }
      return;
    }
    
    if (name === 'numberOfSemesters') {
      const numValue = Number(value);
      setFormData(prev => ({ ...prev, [name]: numValue }));
      // Clear even semester errors if reducing semesters
      if (numValue < 2) {
        setFormErrors(prev => ({
          ...prev,
          evenSemesterDates: undefined
        }));
      }
      return;
    }
    
    if (name.startsWith('oddSemester.')) {
      const field = name.split('.')[1];
      setFormData(prev => ({
        ...prev,
        oddSemesterDates: {
          ...prev.oddSemesterDates,
          [field]: value
        }
      }));
    } else if (name.startsWith('evenSemester.')) {
      const field = name.split('.')[1];
      setFormData(prev => ({
        ...prev,
        evenSemesterDates: {
          ...prev.evenSemesterDates,
          [field]: value
        }
      }));
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
    
    // Clear error when user starts typing
    if (formErrors[name]) {
      setFormErrors(prev => ({ ...prev, [name]: undefined }));
    }
    
    // Mark field as touched
    setTouchedFields(prev => ({ ...prev, [name]: true }));
  };

  // Utility to provide input classes for form fields (used by AcademicYearForm)
  const getInputClasses = (fieldName) => {
    // Determine if there's an error for this field (handles nested error objects)
    const hasError = Object.keys(formErrors).some(key => {
      if (key === fieldName) return true;
      const val = formErrors[key];
      if (val && typeof val === 'object') {
        // check nested keys like oddSemesterDates.startDate or evenSemesterDates.endDate
        return Object.keys(val).some(sub => {
          // e.g. fieldName might be "oddSemester.startDate" or "oddSemesterDates.startDate"
          return `${key}.${sub}` === fieldName || `${key.replace(/Dates$/,'')}.${sub}` === fieldName || `${key}.${sub}`.endsWith(fieldName);
        });
      }
      return false;
    });

    return `w-full px-4 py-3 rounded-lg border transition-colors focus:outline-none ${
      hasError ? 'border-red-500 focus:ring-2 focus:ring-red-200' : 'border-gray-200 focus:ring-2 focus:ring-blue-100'
    }`;
  };

  // Allow viewing calendar from ArchivedAcademicYears modal
  const handleViewCalendar = async (academicYearId) => {
    // close archived modal and open calendar for the selected academic year
    setShowArchivedYears(false);
    await openCalendar(academicYearId);
  };

  // Calendar functions
  const fetchCalendarConfig = async (academicYearId) => {
    if (!academicYearId || !tenantId) return null;
    try {
      const token = localStorage.getItem('token');
      const response = await api.get(
        `/api/academic-year/${academicYearId}/calendar/config?tenantId=${tenantId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const cfg = response.data?.config ?? response.data ?? null;
      const normalized = cfg && cfg.academicYear ? cfg : (response.data?.academicYear ? { academicYear: response.data.academicYear, ...cfg } : cfg);
      setCalendarConfig(normalized);
      return normalized;
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load calendar configuration');
      return null;
    }
  };

  const fetchCalendarEvents = async (academicYearId, semesterType = null) => {
    if (!academicYearId || !tenantId) return null;
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      let url = `/api/academic-year/${academicYearId}/calendar/events?tenantId=${tenantId}`;
      if (semesterType) {
        url += `&semesterType=${semesterType}`;
      }
      const response = await api.get(url, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCalendarEvents(response.data.events || []);
      return response.data.events;
    } catch (err) {
      if (err.response?.status === 404) {
        setCalendarEvents([]);
        return [];
      }
      setError(err.response?.data?.message || 'Failed to load calendar events');
      return null;
    } finally {
      setLoading(false);
    }
  };

  const openCalendar = async (academicYearId) => {
    setSelectedAcademicYearId(academicYearId);
    setShowCalendar(true);
    setError(null);
    setSuccessMessage(null);
    const config = await fetchCalendarConfig(academicYearId);
    if (config) {
      if (config.academicYear.isSemesterBased) {
        setSelectedSemesterType(null);
      } else {
        await fetchCalendarEvents(academicYearId);
      }
    }
  };

  const closeCalendar = () => {
    setShowCalendar(false);
    setSelectedAcademicYearId(null);
    setCalendarConfig(null);
    setSelectedSemesterType(null);
    setCalendarEvents([]);
    setError(null);
    setSuccessMessage(null);
  };

  const handleSemesterSelection = async (semesterType) => {
    setSelectedSemesterType(semesterType);
    await fetchCalendarEvents(selectedAcademicYearId, semesterType);
  };

  // Event handlers
  const handleAddEvent = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    const token = localStorage.getItem('token');
    if (!token) {
      setError('Session expired. Please log in again.');
      setLoading(false);
      return;
    }

    try {
      if (editEventId) {
        const payload = {
          ...newEvent,
          tenantId,
          academicYearId: selectedAcademicYearId,
          semesterType: calendarConfig?.academicYear?.isSemesterBased ? selectedSemesterType : null,
          createdBy: userId,
        };
        await api.put(
          `/api/academic-year/${selectedAcademicYearId}/calendar/events/${editEventId}?tenantId=${tenantId}`,
          payload,
          { headers: { Authorization: `Bearer ${token}` } }
        );
      } else {
        const start = moment(newEvent.startDate);
        const end = moment(newEvent.endDate);
        let current = start.clone();
        const requests = [];
        while (current.isSameOrBefore(end)) {
          if (current.day() !== 0) {
            const payload = {
              ...newEvent,
              startDate: current.format('YYYY-MM-DD'),
              endDate: current.format('YYYY-MM-DD'),
              tenantId,
              academicYearId: selectedAcademicYearId,
              semesterType: calendarConfig?.academicYear?.isSemesterBased ? selectedSemesterType : null,
              createdBy: userId,
            };
            requests.push(
              api.post(
                `/api/academic-year/${selectedAcademicYearId}/calendar/events?tenantId=${tenantId}`,
                payload,
                { headers: { Authorization: `Bearer ${token}` } }
              )
            );
          }
          current.add(1, 'day');
        }
        await Promise.all(requests);
      }

      setShowAddEventModal(false);
      setEditEventId(null);
      setNewEvent({
        eventName: '',
        eventType: 'academic',
        startDate: '',
        endDate: '',
        description: '',
      });

      await fetchCalendarEvents(selectedAcademicYearId, selectedSemesterType);
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Failed to save event');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteEvent = async () => {
    if (!eventToDelete) return;
    setLoading(true);
    setError(null);
    try {
      const token = localStorage.getItem('token');
      await api.delete(
        `/api/academic-year/${selectedAcademicYearId}/calendar/events/${eventToDelete.id}?tenantId=${tenantId}&semesterType=${selectedSemesterType || 'null'}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setShowDeleteEventModal(false);
      setEventToDelete(null);
      await fetchCalendarEvents(selectedAcademicYearId, selectedSemesterType);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to delete event');
    } finally {
      setLoading(false);
    }
  };

  // Unblock Sunday logic
  const handleUnblockSunday = () => {
    setUnblockedSundays(prev => [...prev, selectedSunday]);
    setShowUnblockModal(false);
    setNewEvent({
      ...newEvent,
      startDate: selectedSunday,
      endDate: selectedSunday,
    });
    setShowAddEventModal(true);
  };



  // Calendar grid event handlers
  const handleDateClick = (dateStr, isSunday, isUnblocked) => {
    if (isSunday && !isUnblocked) {
      setSelectedSunday(dateStr);
      setShowUnblockModal(true);
    } else {
      setNewEvent({
        eventName: '',
        eventType: 'academic',
        startDate: dateStr,
        endDate: dateStr,
        description: '',
      });
      setEditEventId(null);
      setShowAddEventModal(true);
    }
  };

  const handleEventClick = (event) => {
    setNewEvent({
      eventName: event.eventName,
      eventType: event.eventType,
      startDate: moment(event.startDate).format('YYYY-MM-DD'),
      endDate: moment(event.endDate).format('YYYY-MM-DD'),
      description: event.description || '',
    });
    setEditEventId(event.id);
    setShowAddEventModal(true);
  };

  const handleUnblockSundayCalendar = (dateStr) => {
    setSelectedSunday(dateStr);
    setShowUnblockModal(true);
  };

  const handleBlockSunday = (dateStr) => {
    setUnblockedSundays(prev => prev.filter(d => d !== dateStr));
  };

  // Filter academic years for display (derived - renamed to avoid redeclaration with state)
  const filteredActiveAcademicYears = academicYears.filter(ay => !ay.isArchived && ay.status === 'active');
  const filteredInactiveAcademicYears = academicYears.filter(ay => !ay.isArchived && ['inactive', 'completed'].includes(ay.status));
  const hasArchivedYears = academicYears.some(ay => ay.isArchived);
  const hasInactiveYears = filteredInactiveAcademicYears.length > 0;

  // Render academic years based on view mode
  const renderAcademicYears = () => {
    const yearsToRender = viewMode === 'inactive' ? filteredInactiveAcademicYears :
                         viewMode === 'all' ? academicYears.filter(ay => !ay.isArchived) :
                         filteredActiveAcademicYears;

    if (loading && yearsToRender.length === 0) {
      return (
        <div className="flex justify-center items-center py-20">
          <div className="text-center">
            <div className="animate-spin rounded-full h-16 w-16 border-t-4 border-b-4 border-blue-600 mx-auto mb-4"></div>
            <p className="text-gray-600 font-medium">Loading academic years...</p>
          </div>
        </div>
      );
    }

    if (!loading && yearsToRender.length === 0 && !showForm) {
      return (
        <div className="text-center py-20">
          <div className="max-w-md mx-auto">
            <div className="w-24 h-24 bg-gradient-to-r from-blue-100 to-purple-100 rounded-3xl flex items-center justify-center mx-auto mb-6">
              <svg className="w-12 h-12 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
            </div>
            <h3 className="text-2xl font-bold text-gray-900 mb-3">
              {viewMode === 'inactive' ? 'No inactive academic years' : 'No academic years yet'}
            </h3>
            <p className="text-gray-600 mb-8">
              {viewMode === 'inactive' 
                ? 'Inactive and completed academic years will appear here.' 
                : 'Start by creating your first academic year to organize semesters and calendar events.'}
            </p>
            {viewMode !== 'inactive' && (
              <button
                onClick={() => setShowForm(true)}
                className="inline-flex items-center gap-3 px-8 py-4 bg-gradient-to-r from-blue-600 to-blue-700 text-white rounded-2xl hover:from-blue-700 hover:to-blue-800 transition-all duration-300 transform hover:-translate-y-1 shadow-2xl font-semibold"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
                Create First Academic Year
              </button>
            )}
          </div>
        </div>
      );
    }

    return (
      <div className="space-y-8">
        {yearsToRender.map(year => (
          <AcademicYearCard
            key={year.id || year._id}
            academicYear={year}
            onEdit={handleEditAcademicYear}
            onDelete={openDeleteModal}
            onOpenCalendar={openCalendar}
            onMarkCompleted={handleMarkAcademicYearCompleted}
            onMarkSemesterCompleted={handleMarkSemesterTypeCompleted}
            onMarkIndividualSemesterCompleted={handleMarkIndividualSemesterCompleted}
            onRollbackStatus={openRollbackModal}
            onRollbackSemester={handleRollbackSemester}
            onRollbackSemesterType={handleRollbackSemesterTypeStatus}
            onRestoreAcademicYear={handleRestoreAcademicYear}
            onViewStatusHistory={handleViewStatusHistory}
            loading={loading}
            isReadOnly={viewMode === 'inactive'}
            renderSemesterDetails={renderSemesterDetails}
          />
        ))}
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-blue-50/30">
      <div className="container mx-auto px-4 py-8 max-w-7xl">
        {/* Header */}
        <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center mb-12">
          <div className="mb-6 lg:mb-0">
            <h1 className="text-4xl font-bold text-gray-900 mb-3">Academic Years</h1>
            <p className="text-lg text-gray-600 max-w-2xl">
              Manage academic periods, semesters, and calendar events with rollback and recovery options
            </p>
            
            {/* View Mode Tabs */}
            <div className="flex space-x-1 mt-4 bg-gray-100 rounded-2xl p-1 w-fit">
              <button
                onClick={() => setViewMode('active')}
                className={`px-4 py-2 rounded-xl font-medium transition-all duration-200 ${
                  viewMode === 'active'
                    ? 'bg-white text-blue-700 shadow-sm'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                Active
              </button>
              <button
                onClick={() => setViewMode('inactive')}
                className={`px-4 py-2 rounded-xl font-medium transition-all duration-200 ${
                  viewMode === 'inactive'
                    ? 'bg-white text-blue-700 shadow-sm'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                Inactive/Completed
              </button>
              <button
                onClick={() => setViewMode('all')}
                className={`px-4 py-2 rounded-xl font-medium transition-all duration-200 ${
                  viewMode === 'all'
                    ? 'bg-white text-blue-700 shadow-sm'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                All
              </button>
            </div>
          </div>
          
          <div className="flex flex-col sm:flex-row gap-4">
            {hasArchivedYears && (
              <button
                onClick={() => setShowArchivedYears(true)}
                className="flex items-center gap-3 px-6 py-4 bg-orange-500 text-white rounded-2xl hover:bg-orange-600 transition-all duration-300 transform hover:-translate-y-1 shadow-2xl font-semibold"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 8h14M5 8a2 2 0 110-4h14a2 2 0 110 4M5 8v10a2 2 0 002 2h10a2 2 0 002-2V8m-9 4h4" />
                </svg>
                View Archived ({academicYears.filter(ay => ay.isArchived).length})
              </button>
            )}
            {hasInactiveYears && viewMode !== 'inactive' && (
              <button
                onClick={() => setViewMode('inactive')}
                className="flex items-center gap-3 px-6 py-4 bg-gray-500 text-white rounded-2xl hover:bg-gray-600 transition-all duration-300 transform hover:-translate-y-1 shadow-2xl font-semibold"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                </svg>
                View Inactive ({inactiveAcademicYears.length})
              </button>
            )}
            {!showForm && viewMode === 'active' && (
              <button
                onClick={() => setShowForm(true)}
                className="flex items-center gap-3 px-8 py-4 bg-gradient-to-r from-blue-600 to-blue-700 text-white rounded-2xl hover:from-blue-700 hover:to-blue-800 transition-all duration-300 transform hover:-translate-y-1 shadow-2xl font-semibold"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
                New Academic Year
              </button>
            )}
          </div>
        </div>

        {/* Recovery Information Banner */}
        {academicYears.some(ay => ay.status === 'completed' && !ay.isArchived) && (
          <div className="mb-8 p-6 bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-2xl shadow-lg">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center">
                <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <div className="flex-1">
                <h3 className="text-lg font-semibold text-blue-900 mb-2">Recovery Features Available</h3>
                <p className="text-blue-800">
                  Accidentally marked an academic year or semester as completed? Use the rollback feature to revert it. 
                  Archived academic years can be restored within 2 years. All data is preserved for your safety.
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Status Messages */}
        {successMessage && (
          <div 
            ref={successRef}
            tabIndex={-1}
            className="mb-8 flex items-center justify-between p-6 bg-gradient-to-r from-green-50 to-emerald-50 border border-green-200 rounded-2xl shadow-lg focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2"
          >
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <svg className="h-6 w-6 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <div className="ml-4">
                <p className="text-sm font-semibold text-green-800">{successMessage}</p>
              </div>
            </div>
            <button
              onClick={() => setSuccessMessage(null)}
              className="ml-4 text-green-700 hover:text-green-900 rounded-lg focus:outline-none p-1 hover:bg-green-100 transition-colors"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        )}

        {error && (
          <div 
            ref={errorRef}
            tabIndex={-1}
            className="mb-8 flex items-center justify-between p-6 bg-gradient-to-r from-red-50 to-rose-50 border border-red-200 rounded-2xl shadow-lg focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2"
          >
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <svg className="h-6 w-6 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
              </div>
              <div className="ml-4">
                <p className="text-sm font-semibold text-red-800">{error}</p>
              </div>
            </div>
            <button
              onClick={() => setError(null)}
              className="ml-4 text-red-700 hover:text-red-900 rounded-lg focus:outline-none p-1 hover:bg-red-100 transition-colors"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        )}

        {/* Form Validation Summary */}
        {showForm && Object.keys(formErrors).length > 0 && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-xl">
            <div className="flex items-center mb-2">
              <svg className="w-5 h-5 text-red-500 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
              <h3 className="text-red-800 font-semibold">Please fix the following errors:</h3>
            </div>
            <ul className="text-red-700 text-sm list-disc list-inside space-y-1">
              {formErrors.name && <li>{formErrors.name}</li>}
              {formErrors.label && <li>{formErrors.label}</li>} {/* NEW: Display label error */}
              {formErrors.startDate && <li>{formErrors.startDate}</li>}
              {formErrors.endDate && <li>{formErrors.endDate}</li>}
              {formErrors.numberOfSemesters && <li>{formErrors.numberOfSemesters}</li>}
              {formErrors.oddSemesterDates?.startDate && <li>{formErrors.oddSemesterDates.startDate}</li>}
              {formErrors.oddSemesterDates?.endDate && <li>{formErrors.oddSemesterDates.endDate}</li>}
              {formErrors.evenSemesterDates?.startDate && <li>{formErrors.evenSemesterDates.startDate}</li>}
              {formErrors.evenSemesterDates?.endDate && <li>{formErrors.evenSemesterDates.endDate}</li>}
            </ul>
          </div>
        )}

        {/* Academic Years List */}
        {!showForm && renderAcademicYears()}

        {/* Academic Year Form */}
        {showForm && (
          <AcademicYearForm
            formData={formData}
            setFormData={setFormData}
            loading={loading}
            editAcademicYearId={editAcademicYearId}
            handleSubmitAcademicYear={handleSubmitAcademicYear}
            resetForm={resetForm}
            handleInputChange={handleInputChange}
            formErrors={formErrors}
            touchedFields={touchedFields}
            onFieldBlur={handleFieldBlur}
            onSemesterFieldBlur={handleSemesterFieldBlur}
            getInputClasses={getInputClasses}
          />
        )}

        {/* Duplicate Confirmation Modal */}
        <DuplicateConfirmationModal
          show={showDuplicateModal}
          onClose={handleCancelDuplicate}
          onConfirm={handleConfirmDuplicate}
          conflictInfo={conflictInfo}
          duplicateType={duplicateType}
          isSubmitting={isSubmitting}
          isEdit={!!editAcademicYearId}
        />

        {/* Delete Confirmation Modal */}
        <DeleteModal
          show={showDeleteModal}
          loading={loading}
          onClose={closeDeleteModal}
          onDelete={handleDeleteAcademicYear}
          title="Archive Academic Year"
          description="This will archive the academic year. All data will be preserved for 2 years and can be restored during this period."
          dangerList={[
            "Academic year will be moved to archives",
            "Data preserved for 2 years",
            "Can be restored anytime within 2 years",
            "After 2 years, data will be permanently deleted"
          ]}
        />

        {/* Status History Modal */}
        <StatusHistoryModal
          show={showStatusHistory}
          onClose={() => setShowStatusHistory(false)}
          statusHistory={statusHistoryData?.history}
          currentStatus={statusHistoryData?.currentStatus}
          academicYearName={statusHistoryData?.academicYearName}
        />

        {/* Rollback Modal */}
        <RollbackModal
          show={showRollbackModal}
          onClose={closeRollbackModal}
          academicYearId={rollbackAcademicYearId}
          academicYears={academicYears}
          onRollbackAcademicYear={handleRollbackAcademicYearStatus}
          onRollbackSemesterType={handleRollbackSemesterTypeStatus}
          loading={loading}
        />

        {/* Archived Academic Years */}
        {showArchivedYears && (
          <ArchivedAcademicYears
            tenantId={tenantId}
            userId={userId}
            onRestoreAcademicYear={handleRestoreAcademicYear}
            onClose={() => setShowArchivedYears(false)}
            onViewCalendar={handleViewCalendar}
          />
        )}

        {/* Calendar Modal */}
        {showCalendar && (
          <div className="fixed inset-0 z-50 overflow-auto bg-black bg-opacity-60 flex items-center justify-center p-4">
            <div className="bg-white rounded-3xl shadow-2xl w-full max-w-7xl max-h-[90vh] overflow-hidden border border-blue-200 flex flex-col">
              <div className="p-6 border-b flex items-center justify-between flex-shrink-0">
                <div>
                  <h2 className="text-2xl md:text-3xl font-bold text-gray-900">
                    Academic Calendar - {calendarConfig?.academicYear?.name}
                  </h2>
                  <p className="text-gray-600 mt-1">
                    {moment(calendarConfig?.academicYear?.startDate).format('MMM DD, YYYY')} - {moment(calendarConfig?.academicYear?.endDate).format('MMM DD, YYYY')}
                  </p>
                </div>
                <button
                  onClick={closeCalendar}
                  className="flex items-center gap-2 px-4 py-2 bg-gray-100 text-gray-700 rounded-xl hover:bg-gray-200 transition-all duration-200 font-semibold"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                  Close
                </button>
              </div>

              <div ref={modalBodyRef} className="flex-1 overflow-y-auto p-6 space-y-6">
                {calendarConfig?.academicYear?.isSemesterBased && !selectedSemesterType && (
                  <div>
                    <SemesterSelector
                      show={true}
                      semesterOptions={calendarConfig.semesterOptions || []}
                      onSelect={handleSemesterSelection}
                    />
                  </div>
                )}

                {(!calendarConfig?.academicYear?.isSemesterBased || selectedSemesterType) && (
                  <div>
                    <CalendarGrid
                      calendarConfig={calendarConfig}
                      selectedSemesterType={selectedSemesterType}
                      calendarEvents={calendarEvents}
                      unblockedSundays={unblockedSundays}
                      onDateClick={handleDateClick}
                      onEventClick={handleEventClick}
                      onUnblockSunday={handleUnblockSundayCalendar}
                      onBlockSunday={handleBlockSunday}
                      onEditEvent={handleEventClick}
                      onDeleteEvent={(event) => {
                        setEventToDelete(event);
                        setShowDeleteEventModal(true);
                      }}
                      eventListRef={eventListRef}
                      onViewAllEvents={handleViewAllEvents}
                      tenantId={tenantId}
                    />
                  </div>
                )}

                {calendarEvents.length > 0 && (
                  <div id="events-section" data-events-section ref={eventsRef} className="border-t border-gray-200 pt-6">
                    <EventList
                      events={calendarEvents}
                      onEdit={handleEventClick}
                      onDelete={(event) => {
                        setEventToDelete(event);
                        setShowDeleteEventModal(true);
                      }}
                      eventListRef={eventListRef}
                    />
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Event Modals */}
        {showAddEventModal && (
          <EventModal
            show={showAddEventModal}
            onClose={() => {
              setShowAddEventModal(false);
              setEditEventId(null);
              setNewEvent({
                eventName: '',
                eventType: 'academic',
                startDate: '',
                endDate: '',
                description: '',
              });
            }}
            event={newEvent}
            onChange={setNewEvent}
            onSubmit={handleAddEvent}
            loading={loading}
            isEdit={!!editEventId}
          />
        )}

        {showDeleteEventModal && (
          <DeleteModal
            show={showDeleteEventModal}
            loading={loading}
            onClose={() => {
              setShowDeleteEventModal(false);
              setEventToDelete(null);
            }}
            onDelete={handleDeleteEvent}
            title="Delete Event"
            description="Are you sure you want to delete this event? This action cannot be undone."
            dangerList={["This event will be permanently removed from the calendar"]}
          />
        )}

        {showUnblockModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-60">
            <div className="bg-white rounded-3xl shadow-2xl max-w-md w-full mx-4 border border-orange-200">
              <div className="p-8">
                <div className="flex items-start mb-6">
                  <div className="flex-shrink-0">
                    <div className="w-12 h-12 bg-orange-100 rounded-2xl flex items-center justify-center">
                      <svg className="w-6 h-6 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                    </svg>
                    </div>
                  </div>
                  <div className="ml-6">
                    <h3 className="text-xl font-bold text-gray-900 mb-2">Unblock Sunday</h3>
                    <p className="text-gray-600">
                      Sundays are typically blocked. Do you want to unblock {moment(selectedSunday).format('MMM DD, YYYY')} and add an event?
                    </p>
                  </div>
                </div>
                <div className="flex gap-4">
                  <button
                    onClick={() => setShowUnblockModal(false)}
                    className="flex-1 px-6 py-3 bg-gray-100 text-gray-700 rounded-xl hover:bg-gray-200 transition-all duration-200 font-semibold"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleUnblockSunday}
                    className="flex-1 px-6 py-3 bg-orange-500 text-white rounded-xl hover:bg-orange-600 transition-all duration-200 font-semibold"
                  >
                    Unblock & Add Event
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {showArchivedModal && (
          <ArchivedAcademicYears
            tenantId={tenantId}
            userId={userId}
            onRestoreAcademicYear={handleRestoreAcademicYear}
            onClose={() => setShowArchivedModal(false)}
            onViewCalendar={handleViewCalendar}
          />
        )}
      </div>
    </div>
  );
};

export default AcademicYear;